import type { BiddingApprovalStageApprovers } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

/** 案件 1 件についての承認者の識別結果 (AC-BSP-098)。 */
export type ApproverIdentity = {
  /** 「承認者ではありません」 の注意を出すか。 */
  isNotApprover: boolean
  /** 「自己承認」 の注意を出すか。 */
  isSelfApproval: boolean
  /** 注意に出す承認者の表示名を連結したもの (0 人なら「設定なし」)。 */
  approversLabel: string
}

/**
 * 承認者を引く対象 (通常入札の申請セット・ブロック入札のグループ)。 `stageApprovers` は
 * 承認申請の時点の写しのうち、 次の承認・差戻しを判定する段のエリアごとの承認者 (段の決め方は
 * backend が持つ)。
 */
export type ApproverTarget = {
  stageApprovers: readonly BiddingApprovalStageApprovers[]
  requestedBy?: string
}

/** 案件から承認者の識別結果を引く resolver。 */
export type ApproverIdentityResolver = (
  target: ApproverTarget,
) => ApproverIdentity

/** 現在ユーザーのセッション取得状態 (`useCurrentSession`)。 */
export type ApproverSessionState = {
  isPending: boolean
  isError: boolean
  memberId: string | undefined
}

/** 承認者でない・自己承認の判定と、 注意に出す承認者の member ID。 */
export type ApproverJudgement = Pick<
  ApproverIdentity,
  'isNotApprover' | 'isSelfApproval'
> & {
  /** 現在ユーザーを含まないエリアの当該段の承認者 (重複なし、 写しの並び順)。 */
  noticeApproverMemberIds: string[]
}

/**
 * 承認申請の時点の写しから、 承認者でない・自己承認の注意を出すかを決める
 * (`docs/specs/conventions.md`「設定・マスタを変えたときの進行中のものの扱い」 原則 1)。
 *
 * 判定する段の全エリアの承認者に含まれる時だけ承認者で、 行が無ければ承認者 0 人。
 * セッションの読み込み中は承認者でない旨を出さない。 取得失敗は member_id を未確定として扱い、
 * 承認者でない扱いにして自己承認とは判定しない。
 */
export function judgeApproverIdentity(
  target: ApproverTarget,
  session: ApproverSessionState,
): ApproverJudgement {
  const memberId =
    session.isError || session.memberId === '' ? undefined : session.memberId
  const rowsWithoutMe = target.stageApprovers.filter(
    (row) =>
      memberId === undefined || !row.approverMemberIds.includes(memberId),
  )
  const isApprover =
    target.stageApprovers.length > 0 && rowsWithoutMe.length === 0
  return {
    isNotApprover: !session.isPending && !isApprover,
    isSelfApproval: memberId !== undefined && target.requestedBy === memberId,
    noticeApproverMemberIds: [
      ...new Set(rowsWithoutMe.flatMap((row) => row.approverMemberIds)),
    ],
  }
}

/**
 * 承認・差戻しボタンの近くに常時出す注意の文。 承認者でなく申請者本人でもある時は、
 * 2 本に分けず 1 文にまとめる (alert-placement §3: 両方の事実が要る時は 1 本の文にする)。
 */
export function buildApproverStandingNotices(
  identity: ApproverIdentity,
  t: TFunction,
): string[] {
  if (identity.isNotApprover && identity.isSelfApproval) {
    return [
      t('page:biddingSpot.approverWarning.notApproverAndSelf', {
        approvers: identity.approversLabel,
      }),
    ]
  }
  return [
    ...(identity.isNotApprover
      ? [
          t('page:biddingSpot.approverWarning.notApprover', {
            approvers: identity.approversLabel,
          }),
        ]
      : []),
    ...(identity.isSelfApproval
      ? [t('page:biddingSpot.approverWarning.selfApproval')]
      : []),
  ]
}

/** 承認確認モーダル・差戻しダイアログに出す再確認の文。 */
export function buildApproverConfirmNotices(
  identity: ApproverIdentity,
  action: 'approve' | 'reject',
  t: TFunction,
): string[] {
  return [
    ...(identity.isNotApprover
      ? [
          t(
            action === 'approve'
              ? 'page:biddingSpot.approverWarning.notApproverConfirmApprove'
              : 'page:biddingSpot.approverWarning.notApproverConfirmReject',
          ),
        ]
      : []),
    ...(identity.isSelfApproval
      ? [t('page:biddingSpot.approverWarning.selfApproval')]
      : []),
  ]
}
